// Research Drawer — a 480px slide-in that shows the REAL record behind
// a cleanup issue. The #2 UX failure was "unable to click on either
// Peta Simpson to see details" — a steward can't decide a merge they
// can't inspect. Any RecRef (clickable record name in the queue or
// research pane) opens this. Lazy-mounted: only fetches when open.
//
// Reuses the existing read APIs (api.getContact → SafeContact,
// api.getHousehold → HouseholdDetail) — no new endpoints. Identity /
// household / services / a "open full record" deep link, matching the
// design's drawer body sections.

import { useEffect, useState } from "react";
import { ExternalLink, Pencil, X } from "lucide-react";

import {
  api,
  ApiError,
  type ContactDetailResponse,
  type HouseholdDetail,
  type SafeContact,
  type ServiceRecordDetailResponse,
  type TransactionDetail,
} from "../api";
import { realtySide, realtySideLabel } from "@shared/realty/transaction-side";
import { humanizeServiceType } from "@shared/db/service-type-labels";
import { formatCurrency } from "../utils/formatters";
import type { RecordRef } from "./cleanup-records";
import { displayLabel, kindLabel } from "./cleanup-records";
import {
  householdMemberNames,
  transactionContext,
} from "./transaction-context";
import {
  canArchiveFromDrawer,
  drawerFetchKind,
  isRecordOnIssue,
  recordDeepLink,
} from "./record-links";
import {
  ContactInlineEdit,
  HouseholdInlineEdit,
  type InlineSaveResult,
} from "./CleanupInlineEdit";
import { MemberRoleControl } from "../components/MemberRoleControl";
import {
  CleanupToastHost,
  savedToast,
  type CleanupToastMessage,
} from "./cleanup-toast";

function fullName(c: SafeContact): string {
  const n = [
    c.preferred_name ?? c.legal_first_name,
    c.legal_last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  return displayLabel(n) || c.email || "(no name on file)";
}

function addr(c: SafeContact): string | null {
  const a = [
    c.street_line_1,
    c.street_line_2,
    [c.city, c.state].filter(Boolean).join(", "),
    c.zip,
  ]
    .filter(Boolean)
    .join(" · ");
  return a || null;
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--fg-muted)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13.5,
          color: value ? "var(--navy)" : "var(--fg-muted)",
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "var(--fg-muted)",
        margin: "14px 0 6px",
      }}
    >
      {children}
    </div>
  );
}

function ContactBody({ d }: { d: ContactDetailResponse }) {
  const c = d.contact;
  const services = d.company_relationships ?? [];
  const opps = d.household_opportunities ?? [];
  const txns = d.household_transactions ?? [];
  const rhythms = d.household_rhythms ?? [];
  const roles = d.contact_roles ?? [];
  const nothingAttached =
    services.length === 0 &&
    opps.length === 0 &&
    txns.length === 0 &&
    rhythms.length === 0 &&
    roles.length === 0;
  return (
    <div>
      <Field label="Name" value={fullName(c)} />
      <Field label="Email" value={c.email} />
      {c.secondary_email && (
        <Field label="Secondary email" value={c.secondary_email} />
      )}
      <Field label="Phone" value={c.phone_primary} />
      {c.phone_secondary && (
        <Field label="Alt phone" value={c.phone_secondary} />
      )}
      <Field label="Birthday" value={c.birthday} />
      <Field label="Address" value={addr(c)} />
      <Field
        label="Type"
        value={c.contact_type === "vendor" ? "Vendor" : "Person"}
      />
      <Field
        label="Household"
        value={
          d.household
            ? `${displayLabel(d.household.display_name)} · ${d.household.contact_count} member(s)`
            : "Not in a household"
        }
      />

      {c.archived_at && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 10px",
            background: "rgba(192,57,43,0.08)",
            border: "1px solid var(--danger)",
            borderRadius: 8,
            fontSize: 12.5,
            color: "var(--danger)",
          }}
        >
          Archived {c.archived_at}
          {c.archived_reason ? ` — ${c.archived_reason}` : ""}
        </div>
      )}
      {(c.has_ssn_on_file ||
        c.has_bank_account_on_file ||
        c.has_drivers_license_on_file) && (
        <div
          style={{ marginTop: 8, fontSize: 12, color: "var(--warning)" }}
        >
          Sensitive data on file (SSN / DL / bank) — admin-gated on merge.
        </div>
      )}

      {/* What's attached — Grant: "the thing I am NOT seeing is the
          things attached to the contact like policies/transactions/
          opportunities." */}
      <SectionHead>Attached records</SectionHead>
      {nothingAttached ? (
        <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
          No services, opportunities, or transactions linked.
        </div>
      ) : (
        <div style={{ fontSize: 13, color: "var(--navy)" }}>
          {services.map((s) => (
            <div key={s.company_id} style={{ marginBottom: 4 }}>
              {s.company_display_name}
              <span style={{ color: "var(--fg-muted)" }}>
                {" "}
                · {s.relationship_state}
                {s.current_advisor_name
                  ? ` · ${s.current_advisor_name}`
                  : ""}
              </span>
            </div>
          ))}
          {opps.length > 0 && (
            <div style={{ marginTop: 4, color: "var(--fg-muted)" }}>
              {opps.length} open opportunit
              {opps.length === 1 ? "y" : "ies"}
            </div>
          )}
          {txns.length > 0 && (
            <div style={{ color: "var(--fg-muted)" }}>
              {txns.length} open transaction
              {txns.length === 1 ? "" : "s"}
            </div>
          )}
          {rhythms.length > 0 && (
            <div style={{ color: "var(--fg-muted)" }}>
              {rhythms.length} active care plan
              {rhythms.length === 1 ? "" : "s"}
            </div>
          )}
          {roles.length > 0 && (
            <div style={{ color: "var(--fg-muted)" }}>
              {roles.length} role assignment
              {roles.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
      <div
        style={{
          marginTop: 10,
          padding: "8px 10px",
          background: "var(--blue-tint)",
          borderRadius: 8,
          fontSize: 11.5,
          color: "var(--fg-3)",
          lineHeight: 1.45,
        }}
      >
        On a contact merge, activities, notes, and tags move to the
        survivor. Services, opportunities, and transactions are
        household-scoped — they stay with their household. To
        consolidate everything, merge the households.
      </div>
    </div>
  );
}

// Short per-member address line (city/state, else zip, else street) so
// two members with the same name are still tellable apart.
function memberAddr(m: HouseholdDetail["contacts"][number]): string | null {
  return (
    [m.city, m.state].filter(Boolean).join(", ") ||
    m.zip ||
    m.street_line_1 ||
    null
  );
}

function memberName(m: HouseholdDetail["contacts"][number]): string {
  return (
    displayLabel(
      [m.preferred_name ?? m.legal_first_name, m.legal_last_name]
        .filter(Boolean)
        .join(" "),
    ) || "(no name)"
  );
}

function HouseholdBody({
  h,
  onRoleChanged,
  onRoleError,
}: {
  h: HouseholdDetail;
  // Donna 2026-07-31 — changing a member's role is done HERE, in the
  // queue, not by navigating away. Undefined only if a host mounts this
  // body read-only; the workspace always supplies both.
  onRoleChanged?: (message: string) => void;
  onRoleError?: (message: string) => void;
}) {
  const hh = h.household;
  const place =
    [hh.primary_city, hh.primary_state].filter(Boolean).join(", ") ||
    hh.primary_zip ||
    null;
  // Structural anomalies Brian flagged (2026-07-07): a household with
  // two members sharing an identical name, and/or more than one member
  // marked "primary". These almost always mean a DUPLICATE CONTACT was
  // imported into the household (e.g. two Schwab account rows for the
  // same person), not two real people — so we call it out plainly and
  // point at the fix (merge the duplicate contacts / fix the roles).
  const primaryCount = h.contacts.filter(
    (m) => (m.household_role ?? "").toLowerCase() === "primary",
  ).length;
  const nameCounts = new Map<string, number>();
  for (const m of h.contacts) {
    const key = memberName(m).toLowerCase();
    if (key && key !== "(no name)") {
      nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }
  }
  const duplicateNames = [...nameCounts.values()].some((n) => n > 1);
  const anomaly = duplicateNames || primaryCount > 1;
  // So a promotion's confirmation can name who got demoted.
  const memberNamesById = Object.fromEntries(
    h.contacts.map((m) => [m.id, memberName(m)]),
  );
  return (
    <div>
      <Field label="Household" value={displayLabel(hh.display_name)} />
      <Field label="Location" value={place} />
      <Field label="Client since" value={hh.client_since} />
      {hh.is_quarantined && (
        <div
          style={{
            margin: "4px 0 10px",
            padding: "8px 10px",
            background: "rgba(198,146,32,0.1)",
            border: "1px solid var(--warning)",
            borderRadius: 8,
            fontSize: 12.5,
            color: "var(--warning)",
          }}
        >
          Quarantined{hh.quarantine_reason ? ` — ${hh.quarantine_reason}` : ""}
        </div>
      )}
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--fg-muted)",
          margin: "12px 0 6px",
        }}
      >
        Members ({h.contacts.length})
      </div>
      {anomaly && (
        <div
          style={{
            margin: "0 0 8px",
            padding: "8px 10px",
            background: "rgba(198,146,32,0.1)",
            border: "1px solid var(--warning)",
            borderRadius: 8,
            fontSize: 12.5,
            color: "var(--attention)",
            lineHeight: 1.45,
          }}
        >
          {duplicateNames ? (
            <>
              Two members share the same name
              {primaryCount > 1 ? ", and more than one is marked “primary.”" : "."}{" "}
              A household normally has one person per member — this is almost
              always a <b>duplicate contact</b> from an import (two account
              rows for the same person), not two different people. Click a
              member below to open its full contact record, then use{" "}
              <b>Merge</b> there to fold the duplicate into the real contact
              (unless it’s genuinely a Jr./Sr.) — or look for this same
              household in the Cleanup queue, since same-name-in-household
              duplicates are detected and routed to a merge action
              automatically.
            </>
          ) : (
            <>
              {primaryCount} members are marked “primary,” but a household
              has just one primary contact. <b>Set the extra member’s role
              right here</b> — pick Spouse, Child or Other on their row
              below. Whoever stays the primary keeps that role.
            </>
          )}
        </div>
      )}
      {h.contacts.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 8 }}>
          No contacts in this household.
        </div>
      ) : (
        h.contacts.map((m) => {
          const details = [
            m.email,
            m.phone_primary,
            m.secondary_email,
            m.phone_secondary,
            memberAddr(m),
            m.birthday ? `b. ${m.birthday}` : null,
          ].filter(Boolean);
          const memberLink = recordDeepLink("contact", m.id);
          return (
            <div
              key={m.id}
              style={{
                border: "1px solid var(--mid-gray)",
                borderRadius: 8,
                padding: "7px 10px",
                marginBottom: 6,
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 700 }}>
                {memberLink ? (
                  <a
                    href={memberLink}
                    target="_blank"
                    rel="noreferrer"
                    title={`Open ${memberName(m)}'s full contact record`}
                    style={{ color: "var(--navy)", textDecoration: "underline" }}
                  >
                    {memberName(m)}
                  </a>
                ) : (
                  <span style={{ color: "var(--navy)" }}>{memberName(m)}</span>
                )}
                {/* The role is a live control when the host can write,
                    and plain text otherwise — never both, so the row
                    can't show two different answers mid-save. */}
                {onRoleChanged && onRoleError ? (
                  <span style={{ marginLeft: 8, fontWeight: 400 }}>
                    <MemberRoleControl
                      householdId={hh.id}
                      contactId={m.id}
                      memberName={memberName(m)}
                      role={m.household_role}
                      isDesignatedPrimary={hh.primary_contact_id === m.id}
                      memberNamesById={memberNamesById}
                      onChanged={onRoleChanged}
                      onError={onRoleError}
                      compact
                    />
                  </span>
                ) : (
                  m.household_role && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: "var(--fg-3)",
                      }}
                    >
                      · {m.household_role}
                    </span>
                  )
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--fg-3)" }}>
                {details.length ? details.join(" · ") : "no contact details on file"}
              </div>
              {/* Last resort disambiguator when two records are otherwise
                  detail-free: the short record id + when it was created. */}
              <div style={{ fontSize: 10.5, color: "var(--fg-muted)", marginTop: 2 }}>
                id …{m.id.slice(-6)}
                {m.created_at ? ` · added ${m.created_at.slice(0, 10)}` : ""}
              </div>
            </div>
          );
        })
      )}
      {h.company_relationships.length > 0 && (
        <>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--fg-muted)",
              margin: "12px 0 6px",
            }}
          >
            Services
          </div>
          {h.company_relationships.map((r) => (
            <div
              key={r.id}
              style={{ fontSize: 13, color: "var(--navy)", marginBottom: 4 }}
            >
              {r.company_display_name}
              <span style={{ color: "var(--fg-muted)" }}>
                {" "}
                · {r.relationship_state}
                {r.service_records.length
                  ? ` · ${r.service_records.length} record(s)`
                  : ""}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// Policy / loan / case / portfolio preview (Joy 2026-07-01). Before
// this, a service-record ref fetched the CONTACT endpoint → 404 → a
// false "record no longer exists" that made Joy skip real issues.
function ServiceRecordBody({ d }: { d: ServiceRecordDetailResponse }) {
  const sr = d.service_record;
  const ip = d.insurance_policy;
  // Realty listings / home purchases carry a buy/sell side + a sale
  // price (the product metric). Surfacing them here answers "which side,
  // and how big?" the same way the transaction body does (Nate's ask).
  const side = realtySide({
    companySlug: sr.company_slug,
    serviceType: sr.service_type,
    serviceFamily: sr.service_family,
  });
  // Grant 2026-07-10 QA: a blank effective_date rendered as a bare "—"
  // right next to a populated expiration read as a stray character
  // ("— → 2027-01-29"), not "this date is missing." One consistent
  // "(not set)" fallback everywhere reads unambiguously either way.
  const dates =
    ip?.effective_date || ip?.expiration_date
      ? `${ip?.effective_date ?? "(not set)"} → ${ip?.expiration_date ?? "(not set)"}`
      : sr.effective_date
        ? `${sr.effective_date} → ${sr.termination_date ?? "(not set)"}`
        : null;
  return (
    <div>
      <Field
        label="Record"
        value={
          displayLabel(sr.display_label ?? "") ||
          humanizeServiceType(sr.service_type)
        }
      />
      {side && <Field label="Side" value={realtySideLabel(side)} />}
      <Field label="Company" value={sr.company_display_name} />
      <Field label="Status" value={sr.status} />
      {side && sr.product_metric != null && (
        <Field label="Sale price" value={formatCurrency(sr.product_metric)} />
      )}
      <Field
        label="Household"
        value={displayLabel(sr.household_display_name)}
      />
      <Field label="Advisor" value={sr.current_advisor_name} />
      {ip && (
        <>
          <Field label="Carrier" value={ip.carrier_name} />
          <Field label="Policy #" value={ip.policy_number} />
          <Field label="Type" value={ip.policy_type} />
        </>
      )}
      <Field label="Effective → expiration" value={dates} />
      {ip && !ip.expiration_date && (
        <div
          style={{
            margin: "4px 0 10px",
            padding: "8px 10px",
            background: "rgba(254,215,102,0.16)",
            border: "1px solid var(--yellow-deep)",
            borderRadius: 8,
            fontSize: 12.5,
            color: "var(--warning)",
          }}
        >
          No expiration date on file — confirm it on the carrier’s site,
          then set it from the issue’s “Verify the expiration” action.
        </div>
      )}
      {sr.external_url && (
        <a
          href={sr.external_url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 13,
            fontWeight: 700,
            color: "var(--blue)",
            textDecoration: "none",
            marginBottom: 10,
          }}
        >
          Open in {sr.external_system ?? "carrier system"}
          <ExternalLink size={13} strokeWidth={2} aria-hidden />
        </a>
      )}
      <div
        style={{
          marginTop: 10,
          padding: "8px 10px",
          background: "var(--blue-tint)",
          borderRadius: 8,
          fontSize: 11.5,
          color: "var(--navy)",
          lineHeight: 1.45,
        }}
      >
        The carrier / servicer stays the source of truth for this
        record — what’s shown here is the OS’s cached reference.
      </div>
    </div>
  );
}

// Processing (transaction) preview (Nate 2026-07-07). A transaction ref
// used to hit the empty "no inline preview" stub, so a steward opening a
// realty cleanup issue couldn't tell WHICH deal it was — "no idea which
// Solis this is" — and left for ActiveCampaign. The transaction detail
// endpoint already carries everything needed to identify the deal at a
// glance: property address, buy/sell side, close date, sale price,
// household + advisor. Household member names ("Robert & Maria Solis")
// come from a best-effort getHousehold — the "which Solis" answer the
// household display name alone can't give.
function TransactionBody({
  t,
  household,
}: {
  t: TransactionDetail;
  household: HouseholdDetail | null;
}) {
  const c = transactionContext(t);
  const members = householdMemberNames(household?.contacts ?? null);
  return (
    <div>
      {/* Lead with the deal identity: product noun + buy/sell side. */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 4,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Record" value={c.noun} />
        </div>
        {c.sideLabel && (
          <span
            style={{
              flexShrink: 0,
              marginTop: 2,
              fontSize: 10.5,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--blue-dark)",
              background: "var(--blue-tint)",
              borderRadius: 9999,
              padding: "3px 10px",
            }}
          >
            {c.sideLabel}
          </span>
        )}
      </div>
      {/* Property is the strongest "which deal" signal — lead with it. */}
      {c.property && <Field label="Property" value={c.property} />}
      <Field label="Household" value={displayLabel(c.household)} />
      <SectionHead>Household members</SectionHead>
      {household == null ? (
        <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
          Loading members…
        </div>
      ) : members.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>
          No named members on file.
        </div>
      ) : (
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: "var(--navy)",
            marginBottom: 4,
          }}
        >
          {members.join(" · ")}
        </div>
      )}

      <SectionHead>Deal</SectionHead>
      <Field label="Company" value={c.company} />
      <Field label="Advisor" value={c.advisor} />
      <Field label="Stage" value={c.stageStatus} />
      <Field
        label={c.priceLabel}
        value={c.price != null ? formatCurrency(c.price) : null}
      />
      <Field label={c.closeDateLabel} value={c.closeDate} />

      <div
        style={{
          marginTop: 10,
          padding: "8px 10px",
          background: "var(--blue-tint)",
          borderRadius: 8,
          fontSize: 11.5,
          color: "var(--navy)",
          lineHeight: 1.45,
        }}
      >
        This is the {c.noun.toLowerCase()} processing record. Open the full
        record for the milestone timeline, conditions, and history.
      </div>
    </div>
  );
}

// Kinds with no drawer preview (opportunity / activity): say so honestly
// and point at the full record page when one exists — never a
// wrong-endpoint fetch that reads as "record no longer exists".
// When the host supplies a flowLink (the issue's real working surface —
// e.g. the commission flow for a commission_reports ref, which has no
// record page of its own), offer it instead of the bare "the evidence
// above is all we hold" dead end (UX rule #5). — Baker 2026-07-30
function NoPreviewBody({
  record,
  flowLink,
}: {
  record: RecordRef;
  flowLink?: { to: string; label: string } | undefined;
}) {
  const link = recordDeepLink(record.kind, record.id);
  return (
    <div style={{ fontSize: 13, color: "var(--fg-3)", lineHeight: 1.5 }}>
      {link ? (
        <>
          This {kindLabel(record.kind)} doesn’t have an inline preview —
          use <b>Open full record</b> below to inspect it.
        </>
      ) : flowLink ? (
        <>
          This {kindLabel(record.kind)} doesn’t have its own record page —
          it lives in its own workflow.{" "}
          <a
            href={flowLink.to}
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--blue)", fontWeight: 700 }}
          >
            {flowLink.label} ↗
          </a>
        </>
      ) : (
        <>
          This {kindLabel(record.kind)} doesn’t have its own record page —
          the issue’s evidence above is the detail we hold for it.
        </>
      )}
    </div>
  );
}

export function CleanupRecordDrawer({
  record,
  onClose,
  onArchive,
  startInEdit,
  onSaved,
  suppressSaveToast,
  flowLink,
  issueRecords,
}: {
  record: RecordRef | null;
  onClose: () => void;
  // Grant 2026-05-18: archive must live where the record's details
  // are visible ("I have no idea which one I'm archiving"). When
  // provided, the drawer shows an "Archive this record" action — the
  // user is looking at the full identity + attached records, so they
  // know exactly what they're archiving. Reversible.
  onArchive?: (r: RecordRef) => Promise<void>;
  // Donna's request: open the drawer straight into the inline editor
  // (the "Edit details" affordance in the decide rail sets this) so a
  // simple name/email/address fix is one click, not a hunt.
  startInEdit?: boolean;
  // Fired after a successful inline save so the host can refresh the
  // cleanup queue / record list (a fixed name may clear a name-caps
  // issue on the next detection pass). The result reports what was
  // edited and whether anything changed.
  onSaved?: (result: InlineSaveResult) => void;
  // When set, the drawer does NOT show its own "Saved" toast — the host
  // owns the feedback (e.g. CleanupProvider, which upgrades it to
  // "Resolved ✓" once the reload shows the issue cleared). Left unset by
  // the workspace, so the drawer self-toasts there.
  suppressSaveToast?: boolean;
  // The issue's real working surface for a record kind with no record
  // page (e.g. the commission flow for a commission_reports "activity"
  // ref) — rendered by NoPreviewBody so the drawer is never a dead end
  // (Baker 2026-07-30). Host-supplied because only the host knows which
  // issue the record belongs to.
  flowLink?: { to: string; label: string };
  // The issue's own records. Archive is offered only on one of these:
  // the decide route archives nothing the issue doesn't list (2026-10-02),
  // so a record reached from research (a duplicate contact's household)
  // gets a pointer to its own page instead of a button that would refuse.
  issueRecords?: ReadonlyArray<{ kind: string; id: string }>;
}) {
  const [contactDetail, setContactDetail] =
    useState<ContactDetailResponse | null>(null);
  const [household, setHousehold] = useState<HouseholdDetail | null>(null);
  const [srDetail, setSrDetail] =
    useState<ServiceRecordDetailResponse | null>(null);
  const [txnDetail, setTxnDetail] = useState<TransactionDetail | null>(null);
  // Loaded separately + in the background (best-effort) so the
  // transaction body paints immediately and member names pop in when the
  // household resolves.
  const [txnHousehold, setTxnHousehold] = useState<HouseholdDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveErr, setArchiveErr] = useState<string | null>(null);
  // Inline edit (Donna). editing → the body shows the field editor
  // instead of the read-only view. reloadKey bumps after a save so the
  // drawer re-fetches and shows the new values.
  const [editing, setEditing] = useState(!!startInEdit);
  const [reloadKey, setReloadKey] = useState(0);
  // Transient save feedback (Fix #6). Owned here so it appears no matter
  // which host mounts the drawer, unless the host suppresses it to own
  // the feedback itself.
  const [toast, setToast] = useState<CleanupToastMessage | null>(null);

  useEffect(() => {
    if (!record) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setContactDetail(null);
    setHousehold(null);
    setSrDetail(null);
    setTxnDetail(null);
    setTxnHousehold(null);
    setArmed(false);
    setArchiving(false);
    setArchiveErr(null);
    void (async () => {
      try {
        // Route by kind (record-links.ts). Fetching the wrong endpoint
        // is how a real policy read as "record no longer exists" — a
        // service_record id is NOT a contact id (Joy 2026-07-01).
        const fetchKind = drawerFetchKind(record.kind);
        if (fetchKind === "household") {
          const h = await api.getHousehold(record.id);
          if (!cancelled) setHousehold(h);
        } else if (fetchKind === "service_record") {
          const r = await api.getServiceRecordDetail(record.id);
          if (!cancelled) setSrDetail(r);
        } else if (fetchKind === "transaction") {
          // The transaction detail identifies the deal (address, side,
          // close date, sale price, advisor). Member names are a
          // best-effort background load — a failure never blocks the
          // body, and the deal is already identified without them.
          const { transaction } = await api.getTransactionDetail(record.id);
          if (!cancelled) setTxnDetail(transaction);
          void api
            .getHousehold(transaction.household_id)
            .then((h) => {
              if (!cancelled) setTxnHousehold(h);
            })
            .catch(() => {
              /* members are a nice-to-have; the deal is already named */
            });
        } else if (fetchKind === "contact") {
          // contact / vendor — both are rows in contacts. The
          // detail endpoint also carries linked services / opps /
          // transactions so the steward sees what's attached.
          const r = await api.getContactDetail(record.id);
          if (!cancelled) setContactDetail(r);
        }
        // fetchKind "none" → NoPreviewBody renders; nothing to load.
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.status === 404
                ? "That record no longer exists (it may have been merged or archived)."
                : err.message
              : "Couldn't load this record.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [record, reloadKey]);

  // Reset edit mode to the caller's intent whenever the inspected
  // record changes (so clicking a new record name doesn't inherit the
  // previous record's open editor).
  useEffect(() => {
    setEditing(!!startInEdit);
  }, [record, startInEdit]);

  useEffect(() => {
    if (!record) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [record, onClose]);

  if (!record) return null;

  const deepLink = recordDeepLink(record.kind, record.id);
  const previewless = drawerFetchKind(record.kind) === "none";

  // A loaded record we can edit inline (contact / vendor → contact
  // editor; household → address editor).
  const editableLoaded =
    (contactDetail != null && record.kind !== "household") ||
    (household != null && record.kind === "household");

  function handleSaved(result: InlineSaveResult) {
    setEditing(false);
    setReloadKey((k) => k + 1);
    // Show the "Saved" confirmation here unless the host owns feedback.
    if (!suppressSaveToast) {
      setToast(savedToast(result.entity, result.changed));
    }
    onSaved?.(result);
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(2,28,49,0.32)",
          zIndex: 250,
        }}
      />
      <div
        role="dialog"
        aria-label={`Record: ${record.label}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          maxWidth: "92vw",
          background: "var(--bg-0)",
          boxShadow: "-8px 0 28px rgba(2,28,49,0.18)",
          zIndex: 260,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--mid-gray)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "var(--blue-dark)",
              }}
            >
              {kindLabel(record.kind)}
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "var(--navy)",
              }}
            >
              {displayLabel(record.label)}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              lineHeight: 1,
              display: "inline-flex",
              padding: 2,
            }}
          >
            <X size={20} strokeWidth={1.75} aria-hidden />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {loading && (
            <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>
              Loading record…
            </div>
          )}
          {error && (
            <div style={{ color: "var(--danger)", fontSize: 13 }}>
              {error}
            </div>
          )}
          {!loading && !error && editing && contactDetail && (
            <ContactInlineEdit
              contact={contactDetail.contact}
              onCancel={() => setEditing(false)}
              onSaved={handleSaved}
            />
          )}
          {!loading && !error && editing && household && (
            <HouseholdInlineEdit
              household={household.household}
              onCancel={() => setEditing(false)}
              onSaved={handleSaved}
            />
          )}
          {!loading && !error && !editing && contactDetail && (
            <ContactBody d={contactDetail} />
          )}
          {!loading && !error && !editing && household && (
            <HouseholdBody
              h={household}
              onRoleChanged={(message) => {
                // Reload so the anomaly banner recomputes from the
                // server's view — if that was the last rival claimant,
                // the warning has to disappear on the same screen the
                // fix happened on, or the steward can't tell it worked.
                setReloadKey((k) => k + 1);
                setToast({ text: message, tone: "success" });
                onSaved?.({ entity: "household", changed: true });
              }}
              onRoleError={(message) =>
                setToast({ text: message, tone: "neutral" })
              }
            />
          )}
          {!loading && !error && !editing && srDetail && (
            <ServiceRecordBody d={srDetail} />
          )}
          {!loading && !error && !editing && txnDetail && (
            <TransactionBody t={txnDetail} household={txnHousehold} />
          )}
          {!loading && !error && !editing && previewless && (
            <NoPreviewBody record={record} flowLink={flowLink} />
          )}
        </div>

        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--mid-gray)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {deepLink ? (
              <a
                href={deepLink}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--blue)",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                Open full record
                <ExternalLink size={13} strokeWidth={2} aria-hidden />
              </a>
            ) : (
              <span />
            )}
            <div style={{ display: "flex", gap: 8 }}>
              {/* Edit details — right here where the fields are shown, so
                  a name / email / address fix never means leaving Cleanup
                  (Donna's request). Hidden while the editor is open (it
                  has its own Save / Cancel). */}
              {editableLoaded && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    border: "1px solid var(--blue)",
                    background: "var(--bg-0)",
                    borderRadius: 8,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--blue-dark)",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Pencil size={14} strokeWidth={1.9} aria-hidden />
                  Edit details
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  border: "1px solid var(--mid-gray)",
                  background: "var(--bg-0)",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--navy)",
                  cursor: "pointer",
                }}
              >
                Back to queue
              </button>
            </div>
          </div>

          {/* Archive — right here, where the record's details are
              visible, so the user knows exactly what they're
              archiving (Grant 2026-05-18). Soft delete, reversible.
              Hidden while editing to keep the two flows distinct.
              Only for contact/household kinds — the cleanup archive
              verb archives contacts and households from here; offering
              it on a policy/opportunity ref would hit the wrong record.
              And only on a record the issue lists: anything else is
              archived from its own page (no dead end — we link there). */}
          {onArchive &&
            canArchiveFromDrawer(record.kind) &&
            issueRecords &&
            !isRecordOnIssue(record, issueRecords) &&
            !loading &&
            !error &&
            !editing && (
            <div
              style={{
                borderTop: "1px dashed var(--mid-gray)",
                paddingTop: 10,
                fontSize: 12.5,
                color: "var(--fg-3)",
                lineHeight: 1.45,
              }}
            >
              This record isn’t part of this issue.{" "}
              {deepLink ? (
                <a
                  href={deepLink}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "var(--blue)",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: 44,
                  }}
                >
                  Archive it from its own page ↗
                </a>
              ) : (
                "Archive it from its own page."
              )}
            </div>
          )}
          {onArchive &&
            canArchiveFromDrawer(record.kind) &&
            (!issueRecords || isRecordOnIssue(record, issueRecords)) &&
            !loading &&
            !error &&
            !editing && (
            <div
              style={{
                borderTop: "1px dashed var(--mid-gray)",
                paddingTop: 10,
              }}
            >
              {!armed ? (
                <button
                  onClick={() => setArmed(true)}
                  disabled={archiving}
                  style={{
                    border: "1px solid var(--danger)",
                    background: "var(--bg-0)",
                    borderRadius: 8,
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--danger)",
                    cursor: "pointer",
                  }}
                >
                  Archive this record (it’s a junk / throwaway record)
                </button>
              ) : (
                <div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: "var(--fg-3)",
                      marginBottom: 8,
                      lineHeight: 1.45,
                    }}
                  >
                    Archive <b>{displayLabel(record.label)}</b>? It’s
                    removed from every list and view but the row + a
                    version snapshot are kept — fully restorable from
                    its History / Archive.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={async () => {
                        setArchiving(true);
                        setArchiveErr(null);
                        try {
                          await onArchive(record);
                          // Parent closes the drawer + refreshes.
                        } catch (e) {
                          setArchiveErr(
                            e instanceof Error
                              ? e.message
                              : "Couldn’t archive — nothing changed.",
                          );
                          setArchiving(false);
                        }
                      }}
                      disabled={archiving}
                      style={{
                        border: "1px solid var(--danger)",
                        background: "var(--danger)",
                        borderRadius: 8,
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--white)",
                        cursor: "pointer",
                      }}
                    >
                      {archiving ? "Archiving…" : "Confirm archive"}
                    </button>
                    <button
                      onClick={() => setArmed(false)}
                      disabled={archiving}
                      style={{
                        border: "1px solid var(--mid-gray)",
                        background: "var(--bg-0)",
                        borderRadius: 8,
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--navy)",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  {archiveErr && (
                    <div
                      style={{
                        marginTop: 8,
                        color: "var(--danger)",
                        fontSize: 12.5,
                      }}
                    >
                      {archiveErr}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <CleanupToastHost toast={toast} onDismiss={() => setToast(null)} />
    </>
  );
}
